import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, defaultIfEmpty, map, of } from 'rxjs';
import { ExtranetService } from '../services/extranet.service';
import { startSignIn } from '../auth/start-sign-in';
import { isAuthBypassed } from '../auth/auth-bypass';

// Deliberately a SEPARATE guard from hotelManagerGuard, not a widened allow-list on it — see that
// guard's own comment. hotelManagerGuard's callers are all non-admin surfaces (dashboard,
// calendar, bookings, properties, settings, team/invite), which assume a selected property; a
// pure platform admin has none. Keeping the guards disjoint means every existing non-admin route
// keeps behaving exactly as before, with zero risk from this addition.
//
// Same resolution path as hotelManagerGuard: GET /api/extranet/access/me, never a B2C token
// claim. Not signed in -> B2C sign-in redirect. Signed in as a hotel/agency manager -> bounced to
// their own home (handoff AC-17.1 — this is also where an admin viewing the product AS a group
// lands if they type an admin URL: GET /me answers as that group's manager while the viewpoint
// is pinned, and "Back to admin" in the banner is the way out). Anyone else -> /unauthorized.
export const adminGuard: CanActivateFn = (_route, state) => {
  const msal = inject(MsalService);
  const router = inject(Router);
  const extranetSvc = inject(ExtranetService);

  // Locally there is no MSAL account to find — the interceptor authenticates with an unsigned dev
  // JWT instead — so only the sign-in step is skipped. Role resolution below still runs against the
  // real server, exactly as in production.
  if (!isAuthBypassed()) {
    const account = msal.instance.getActiveAccount();
    // Not signed in: B2C sign-in, then back to this very page (WI 3861) — see start-sign-in.ts.
    if (!account) return startSignIn(msal, router, state.url);
  }

  return extranetSvc.getMe().pipe(
    map((me) => {
      if (me.role === 'ADMIN') return true;
      const isManager = me.role === 'HOTEL_MANAGER' || me.role === 'AGENCY_MANAGER';
      router.navigate([isManager ? '/extranet/dashboard' : '/unauthorized']);
      return false;
    }),
    catchError(() => {
      router.navigate(['/unauthorized']);
      return of(false);
    }),
    // GET /me completes without a value while the interceptor re-authenticates an expired
    // session by redirect (WI 3861): cancel this navigation, the redirect takes the page over.
    defaultIfEmpty(false),
  );
};
